import { useMemo, useState, useEffect, useRef } from "react";
import { Bell, Clock, Key, Mail, MessageSquare, Shield, Smartphone } from "lucide-react";
import type { UseMutationResult } from "@tanstack/react-query";
import {
  Button,
  Input,
  PillSegmentSwitcher,
  Select,
  SettingsCard,
  Toggle,
} from "../../components/ui";
import { settingsTabLabel } from "../../config/admin-settings";
import { api } from "../../lib/api";
import { toast } from "../../stores/toast";

type SecuritySubTab = "2fa" | "pin" | "sessions" | "sms";

const SECURITY_KEYS = [
  "security.mfa_email_enabled",
  "security.mfa_totp_enabled",
  "security.pin_enabled",
  "security.pin_setup_notify",
  "security.session_timeout_min",
  "security.refresh_token_ttl_days",
  "security.sms_otp_enabled",
  "security.sms_otp_api_username",
  "security.sms_otp_api_password",
  "security.sms_otp_brand_name",
] as const;

const SESSION_TIMEOUT_OPTIONS = [
  { value: "0", label: "Never" },
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "60", label: "1 hour" },
  { value: "240", label: "4 hours" },
  { value: "480", label: "8 hours" },
];

export function SecuritySettingsEditor({
  draft,
  setValue,
  saveMut,
  smtpEnabled,
}: {
  draft: Record<string, unknown>;
  setValue: (key: string, value: unknown) => void;
  saveMut: Pick<UseMutationResult<unknown, Error, string[]>, "mutate" | "isPending">;
  smtpEnabled: boolean;
}) {
  const [subTab, setSubTab] = useState<SecuritySubTab>("2fa");
  const [savedSnapshot, setSavedSnapshot] = useState<Record<string, unknown>>({});
  const [smsBalance, setSmsBalance] = useState<number | null>(null);
  const [smsBalanceBusy, setSmsBalanceBusy] = useState(false);
  const snapshotHydrated = useRef(false);

  useEffect(() => {
    if (snapshotHydrated.current || Object.keys(draft).length === 0) return;
    setSavedSnapshot({ ...draft });
    snapshotHydrated.current = true;
  }, [draft]);

  const cancel = () => {
    for (const key of SECURITY_KEYS) {
      setValue(key, savedSnapshot[key] ?? draft[key]);
    }
  };

  const save = () => {
    saveMut.mutate([...SECURITY_KEYS], {
      onSuccess: () => {
        setSavedSnapshot((prev) => {
          const next = { ...prev };
          for (const key of SECURITY_KEYS) next[key] = draft[key];
          return next;
        });
      },
    });
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <Button variant="ghost" size="sm" disabled={saveMut.isPending} onClick={cancel}>
        Cancel
      </Button>
      <Button size="sm" disabled={saveMut.isPending} onClick={save}>
        Save
      </Button>
    </div>
  );

  const mfaEmail = Boolean(draft["security.mfa_email_enabled"]);
  const smsUsername = String(draft["security.sms_otp_api_username"] ?? "").trim();
  const smsPassword = String(draft["security.sms_otp_api_password"] ?? "");
  const smsSecretSaved = smsPassword === "••••••••";
  const smsCredsReady =
    smsUsername.length > 0 && (smsSecretSaved || smsPassword.trim().length > 0);
  const sessionTimeout = String(Number(draft["security.session_timeout_min"] ?? 0));
  const refreshDaysRaw = draft["security.refresh_token_ttl_days"];
  const refreshDaysDisplay =
    refreshDaysRaw === "" || refreshDaysRaw == null
      ? ""
      : typeof refreshDaysRaw === "string"
        ? refreshDaysRaw
        : Number(refreshDaysRaw) === 0
          ? ""
          : String(refreshDaysRaw);

  const subTabs = useMemo(
    () => [
      { value: "2fa" as const, label: "Two-factor authentication", icon: Shield },
      { value: "pin" as const, label: "PIN login", icon: Key },
      { value: "sessions" as const, label: "Sessions", icon: Clock },
      { value: "sms" as const, label: "SMS verification", icon: MessageSquare },
    ],
    [],
  );

  async function checkSmsBalance() {
    setSmsBalanceBusy(true);
    try {
      const data = await api<{ balance: number }>("/settings/sms-balance", { body: {} });
      setSmsBalance(data.balance);
      toast("success", `SMS credit balance: ${data.balance}`);
    } catch (err) {
      toast("error", err instanceof Error ? err.message : "Could not check SMS balance");
    } finally {
      setSmsBalanceBusy(false);
    }
  }

  return (
    <div className="w-full min-w-0 space-y-5">
      <div>
        <h1 className="font-display text-lg font-bold">{settingsTabLabel("security")}</h1>
        <p className="mt-0.5 text-xs text-muted">
          Control 2FA, PIN re-login, and session lifetime for all users.
        </p>
      </div>

      <PillSegmentSwitcher
        value={subTab}
        onChange={setSubTab}
        options={subTabs.map(({ value, label }) => ({ value, label }))}
      />

      {subTab === "2fa" && (
        <SettingsCard
          title="Two-factor authentication (2FA)"
          description="Allow users to enable 2FA via email OTP or authenticator app."
          footer={footer}
        >
          <div className="space-y-5">
            <div>
              <Toggle
                checked={mfaEmail}
                disabled={!smtpEnabled}
                onChange={(v) => setValue("security.mfa_email_enabled", v)}
                label="2FA via Email"
              />
              <p className="mt-1 flex items-start gap-1.5 text-xs text-muted-2">
                <Mail size={14} className="mt-0.5 shrink-0" />
                {smtpEnabled
                  ? "Users can opt in to email codes at sign-in."
                  : "Configure SMTP in Email Settings first."}
              </p>
            </div>
            <div>
              <Toggle
                checked={Boolean(draft["security.mfa_totp_enabled"])}
                onChange={(v) => setValue("security.mfa_totp_enabled", v)}
                label="2FA via Authenticator (TOTP)"
              />
              <p className="mt-1 flex items-start gap-1.5 text-xs text-muted-2">
                <Smartphone size={14} className="mt-0.5 shrink-0" />
                Users can link Google Authenticator or similar apps.
              </p>
            </div>
          </div>
        </SettingsCard>
      )}

      {subTab === "pin" && (
        <SettingsCard
          title="PIN login"
          description="PIN and notifications for quick re-login after timeout or lock."
          footer={footer}
        >
          <div className="space-y-5">
            <div>
              <p className="mb-2 text-xs font-semibold text-foreground">6-digit PIN login</p>
              <p className="mb-3 text-xs text-muted-2">
                When enabled, users can set a 6-digit PIN. After session timeout or lock, they can re-login with PIN
                instead of full credentials.
              </p>
              <Toggle
                checked={Boolean(draft["security.pin_enabled"])}
                onChange={(v) => setValue("security.pin_enabled", v)}
                label="Enable PIN login"
              />
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold text-foreground">First-time PIN setup</p>
              <p className="mb-3 text-xs text-muted-2">
                When PIN login is enabled, show an in-app notification asking users without a PIN to set one up.
              </p>
              <Toggle
                checked={Boolean(draft["security.pin_setup_notify"])}
                onChange={(v) => setValue("security.pin_setup_notify", v)}
                label="Show PIN setup notification"
              />
              <p className="mt-1 flex items-start gap-1.5 text-xs text-muted-2">
                <Bell size={14} className="mt-0.5 shrink-0" />
                Banner appears until the user sets a PIN.
              </p>
            </div>
          </div>
        </SettingsCard>
      )}

      {subTab === "sessions" && (
        <SettingsCard
          title="Sessions"
          description="Auto-lock and refresh token lifetime."
          footer={footer}
        >
          <div className="space-y-5">
            <div>
              <p className="mb-2 text-xs font-semibold text-foreground">Session timeout</p>
              <p className="mb-3 text-xs text-muted-2">
                Auto-logout after inactivity. If PIN is enabled, users can re-login with PIN instead of full credentials.
              </p>
              <Select
                label="Timeout"
                value={sessionTimeout}
                onChange={(e) => setValue("security.session_timeout_min", Number(e.target.value))}
              >
                {SESSION_TIMEOUT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold text-foreground">Refresh session lifetime</p>
              <p className="mb-3 text-xs text-muted-2">
                How long refresh tokens remain valid for re-authentication without a password. Leave empty to use the
                platform default (30 days).
              </p>
              <Input
                label="Days (1–365)"
                type="number"
                min={0}
                max={365}
                placeholder="Platform default"
                value={refreshDaysDisplay}
                onChange={(e) => setValue("security.refresh_token_ttl_days", e.target.value)}
              />
            </div>
          </div>
        </SettingsCard>
      )}

      {subTab === "sms" && (
        <SettingsCard
          title="SMS phone verification"
          description="Require storefront customers to verify a mobile number via OneWaySMS before using the app."
          footer={footer}
        >
          <div className="space-y-5">
            <div>
              <Toggle
                checked={Boolean(draft["security.sms_otp_enabled"])}
                disabled={!smsCredsReady}
                onChange={(v) => setValue("security.sms_otp_enabled", v)}
                label="Require SMS phone verification"
              />
              <p className="mt-1 flex items-start gap-1.5 text-xs text-muted-2">
                <MessageSquare size={14} className="mt-0.5 shrink-0" />
                {smsCredsReady
                  ? "Customers must verify mobile via SMS OTP once before browsing while signed in."
                  : "Enter OneWaySMS API username and password below before enabling."}
              </p>
            </div>
            <Input
              label="OneWaySMS API username"
              value={String(draft["security.sms_otp_api_username"] ?? "")}
              onChange={(e) => setValue("security.sms_otp_api_username", e.target.value)}
              hint="From your OneWaySMS account API section (not your web login)."
            />
            <Input
              label="SMS brand name"
              value={String(draft["security.sms_otp_brand_name"] ?? "MrGaole")}
              onChange={(e) => setValue("security.sms_otp_brand_name", e.target.value)}
              hint="Must match your registered brand in OneWaySMS. Messages are sent as RM0.00 {brand}:…"
            />
            <Input
              label="OneWaySMS API password"
              type="password"
              value={String(draft["security.sms_otp_api_password"] ?? "")}
              onChange={(e) => setValue("security.sms_otp_api_password", e.target.value)}
              placeholder={smsPassword === "••••••••" ? "••••••••" : undefined}
              hint={
                smsSecretSaved
                  ? "Password saved — leave as •••••••• to keep, or enter a new value."
                  : "Stored encrypted. Sender ID uses INFO per OneWaySMS docs."
              }
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={smsBalanceBusy || !smsCredsReady}
                onClick={() => void checkSmsBalance()}
              >
                Check balance
              </Button>
              {smsBalance != null && (
                <span className="text-xs text-muted">Credit balance: {smsBalance}</span>
              )}
            </div>
          </div>
        </SettingsCard>
      )}
    </div>
  );
}
